import React, { useState, useEffect, Alert, Platform, useCallback } from "react";
import { View, Text, Pressable, StyleSheet, Image, FlatList, TextInput, Dimensions, Picker } from "react-native";
import { getFirestore, collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { app } from "../../firebase";
import { useNavigation, useRoute } from '@react-navigation/native'; // Ensure you import useNavigation and useRoute
import { REACT_APP_AWS_SERVER_URL } from '@env';
import HeaderBar from "../../Widgets/HeaderBar";
import AnalyticsManager from "../../services/AnalyticsManager";

const { width, height } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width <= 1500;
const isLargeDesktop = width > 1500;

const VALID_SEARCH_INDICES = [
"Bedroom", "Bathroom", "Decor", "Health & Personal Care", "Kitchen", "Laundry & Cleaning", "Electronics & Office Supplies", "Storage & Organization"
];

const categoryImages = {
	"Bedroom": require("../../assets/Catalogue/Bedroom.jpg"),
	"Bathroom": require("../../assets/Catalogue/Bathroom.jpg"),
	"Decor": require("../../assets/Catalogue/Decor.jpg"),
	"Health & Personal Care": require("../../assets/Catalogue/Health & Personal Care.jpg"),
	"Kitchen": require("../../assets/Catalogue/Kitchen.jpg"),
	"Laundry & Cleaning": require("../../assets/Catalogue/Laundry & Cleaning.jpg"),
	"Electronics & Office Supplies": require("../../assets/Catalogue/Electronics & Office Supplies.jpg"),
	"Storage & Organization": require("../../assets/Catalogue/Storage & Organization.jpg"),
};

const subCategories = {
	"Bedroom": [
		"Sheets", "Comforter", "Bed In a Bag", "Pillow", "Pillow Protector", "Body Pillow", 
		"Mattress Topper", "Mattress Protector", "Throw Blanket", "Study Pillow", 
		"Space Heaters",
		"Misc"
	],
	"Bathroom": [
		"Towel Set", "Waterproof Speakers",
		"Shower Caddy", "Shower Shoes", "Bathrobe", "Shower Wrap", "Hair Wrap", 
		"Bathroom Rug", "Shower Curtain", "Tension Rod", "Cosmetic Organizer",
		"Misc"
	],
	"Laundry & Cleaning": [
		"Cleaning Bundle", "Laundry Basket", "Hamper", "Laundry Detergent", 
		"Odor Eliminator", "Steamer", "Iron",
		"Ironing Board", "Vacuums", "Laundry Bag", "Broom & Dust Pan", 
		"Garbage Can", "Misc"
	],
	"Electronics & Office Supplies": [
		"Television", "Laptop", "Chargers", "Flash Drive", "Hard Drive", 
		"Extension Cords", "Headphones", "Printer", "Alarm Clock", 
		"Bluetooth Speaker", "Personal Alarm", "Room Security",
		"Firestick", "Alexa/Amazon Speakers",
		"Day Planner", "Backpack", "Pens", "Pencils", "Highlighters", 
		"Notebooks", "Stapler", "Scissors", "Printer Paper", "Sticky Notes", 
		"Dry Erase Markers", "Desk Set", "Computer Mouse",
		"Misc"
	],
	"Decor": ["Throw Rug", "Throw Pillows", "Clip Lamps", "Desk Lamp", "Floor Lamp", "Lounge Seating", "Mirror", "Picture Frames", "String Lights", "Tension Rod", "Vanity Mirror", "Window Curtains", "Wall Safe Adhesive", "Dry Erase/Bulletin Board", "Standing Fan", "Bed Fan/Clip Fan", "Doorstopper", "Lap Desk", "Room Decor", "Faux Plants", "Misc"],
	"Health & Personal Care": ["Hair Dryer", "Styling Tools", "Hair Removal", "First Aid Kit", "Pain Reliever", "Sewing Kit", "Exercise Equipment", "Humidifier", "Diffuser", "Diffuser Oils", "Air Purifier", "Noise Machine", "Tool Set", "Misc"],
	"Kitchen": ["Microwave", "Refrigerator", "Personal Refrigerator", "Coffee Maker", "Rice Cooker", "Cooking Tools", "Smoothie Maker", "Air Fryer", "Dishes", "Silverware", "Water Bottles", "Travel Mug", "Can Opener", "Water Filtering", "Mugs", "Snack Bundles", "Food Storage", "Pots/Pans", "Cooking Utensils", "Misc"],
	"Storage & Organization": ["Bedside Shelf/Caddy", "Closet Organization", "Shoe Storage/Racks", "Hangers", "Trunk/Storage", "Underbed Storage", "Drawers", "Over Door Organization", "Rolling Cart", "Desk Organizer", "Wall Key Organizer", "Moving Bags", "Luggage", "Misc"]
};

const Catalogue = ({ onAddItems, isFromRegistry }) => {
	let LOCAL_SERVER_URL = "http://localhost:3300";
	let AWS_SERVER_URL = REACT_APP_AWS_SERVER_URL;
	
	const isDevelopment = process.env.NODE_ENV === "development"; // Adjust this condition based on your setup
	const SERVER_URL = isDevelopment ? LOCAL_SERVER_URL : AWS_SERVER_URL;
	

	const db = getFirestore(app);
	const [catalogueItems, setCatalogueItems] = useState([]);
	const [selectedItems, setSelectedItems] = useState([]);
	const [viewCategories, setViewCategories] = useState(true);
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 12;
	const [totalItemsCount, setTotalItemsCount] = useState(0);
	const totalPages = Math.ceil(totalItemsCount / itemsPerPage);
	const [selectedSubCategory, setSelectedSubCategory] = useState(null);
	const [visibilitySettings, setVisibilitySettings] = useState({
		hidePrice: false,
		hideImages: false,
		hideAll: false
	});
	const [amazonSearchQuery, setAmazonSearchQuery] = useState("");
	const [amazonSearchResults, setAmazonSearchResults] = useState([]);
	const [isSearchingAmazon, setIsSearchingAmazon] = useState(false);
	const [categories] = useState(VALID_SEARCH_INDICES);
	const [mainSearchQuery, setMainSearchQuery] = useState("");
	const [showSearchResults, setShowSearchResults] = useState(false);

	const navigation = useNavigation();
	const route = useRoute();
	const { isFromLandingPage, isFromHub } = route.params || {}; // Get the parameter from the route

	const handleCategoryChange = useCallback((value) => {
		setSelectedCategory(value);
		setCurrentPage(1);
	}, []);

	const handleBackToLanding = () => {
		navigation.navigate('My Tassel - Welcome'); // Adjust 'LandingPage' to your actual landing page route name
	};

	const handleBackToHub = () => {
		navigation.navigate('Hub'); // Adjust 'Hub' to your actual hub page route name
	};

	useEffect(() => {
		if (selectedCategory) {
			const fetchCatalogueItems = async () => {
				const q = query(collection(db, "catalogueItems"), where("category", "==", selectedCategory));
				const querySnapshot = await getDocs(q);
				const items = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
					.filter((item) => 
						(!searchQuery || item.name.toLowerCase().includes(searchQuery.toLowerCase())) &&
						(!selectedSubCategory || item.subCategory === selectedSubCategory)
					);

				setCatalogueItems(items);
				setTotalItemsCount(items.length);
			};
			fetchCatalogueItems();
		}
	}, [selectedCategory, searchQuery, selectedSubCategory, db]);

	useEffect(() => {
		const fetchVisibilitySettings = async () => {
			try {
				const docRef = doc(db, "maintenance", "catalogueVisibility");
				const docSnap = await getDoc(docRef);

				if (docSnap.exists()) {
					setVisibilitySettings(docSnap.data());
				}
			} catch (error) {
				console.error("Error fetching visibility settings:", error);
			}
		};

		fetchVisibilitySettings();
	}, []);

	const handleSelectCategory = useCallback((category) => {
		setSelectedCategory(category);
		setViewCategories(false);
		setCurrentPage(1);
	}, []);

	const handleBackToCategories = () => {
		setViewCategories(true);
		// Optionally reset selected category here if needed
	};

	const handleSelectItem = (item) => {
		const isItemSelected = selectedItems.some((selectedItem) => selectedItem.id === item.id);
		if (isItemSelected) {
			setSelectedItems(selectedItems.filter((selectedItem) => selectedItem.id !== item.id));
		} else {
			setSelectedItems([...selectedItems, item]);
			
			// Track item view when user selects/shows interest in an item
			try {
				AnalyticsManager.trackRegistryItem('registry_item_viewed', {
					id: item.id,
					name: item.name,
					category: item.category,
					subCategory: item.subCategory,
					price: item.price,
					url: item.url,
					image: item.image
				});
			} catch (trackingError) {
				console.error('Error tracking registry item view:', trackingError);
				// Don't block the main process if tracking fails
			}
		}
		// Trigger a visual feedback update, e.g., by changing the state that affects the item's style
	};

	const truncateDescription = (description, wordLimit = 15) => {
		// First, trim to ensure there's no leading/trailing whitespace
		let cleanedDescription = description.trim();

		// Define the prefix to look for, in a case-insensitive manner
		const aboutThisItemPrefix = "About this item";

		// Use a regex to replace "About this item" and any number of following spaces with an empty string
		cleanedDescription = cleanedDescription.replace(new RegExp(`^${aboutThisItemPrefix}\\s*`, 'i'), '');

		// Split the remaining description into words, considering any amount of whitespace as a single splitter
		const words = cleanedDescription.split(/\s+/);

		// Truncate the description to the word limit, if necessary
		if (words.length > wordLimit) {
			return words.slice(0, wordLimit).join(" ") + "...";
		}

		// console.log(cleanedDescription)

		return cleanedDescription;
	};

	const truncateTitle = (title, wordLimit = 6) => {
		const words = title.split(" ");
		if (words.length > wordLimit) {
			return words.slice(0, wordLimit).join(" ") + "...";
		}
		return title;
	};

	const paginatedItems = useCallback(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		
		const combinedItems = [
			...amazonSearchResults.map(item => ({
				id: item.asin,
				name: item.title,
				price: item.price,
				image: item.imageUrl,
				url: item.affiliateUrl,
				description: item.description,
				category: selectedCategory,
				subCategory: selectedSubCategory,
				isAmazonItem: true,
				relevanceScore: calculateRelevanceScore(item, searchQuery)
			})),
			...catalogueItems.map(item => ({
				...item,
				relevanceScore: calculateRelevanceScore(item, searchQuery)
			}))
		];

		const sortedItems = combinedItems.sort((a, b) => b.relevanceScore - a.relevanceScore);
		return sortedItems.slice(startIndex, startIndex + itemsPerPage);
	}, [amazonSearchResults, catalogueItems, currentPage, itemsPerPage, searchQuery, selectedCategory, selectedSubCategory]);

	const calculateRelevanceScore = (item, query) => {
		if (!query) return 0;
		
		const searchTerms = query.toLowerCase().split(' ');
		let score = 0;
		
		// Check title/name matches
		const titleWords = (item.name || item.title || '').toLowerCase();
		searchTerms.forEach(term => {
			if (titleWords.includes(term)) {
				score += 10; // Higher weight for title matches
			}
		});
		
		// Check description matches
		const description = (item.description || '').toLowerCase();
		searchTerms.forEach(term => {
			if (description.includes(term)) {
				score += 5; // Lower weight for description matches
			}
		});
		
		// Boost score for exact category/subcategory matches
		if (item.category?.toLowerCase().includes(query.toLowerCase())) {
			score += 8;
		}
		if (item.subCategory?.toLowerCase().includes(query.toLowerCase())) {
			score += 8;
		}
		
		return score;
	};

	const handleNextPage = () => {
		if (currentPage < totalPages) {
			setCurrentPage((prevCurrentPage) => prevCurrentPage + 1);
		}
	};

	const handlePreviousPage = () => {
		if (currentPage > 1) {
			setCurrentPage((prevCurrentPage) => prevCurrentPage - 1);
		}
	};

	const handleAddItems = async () => {
		if (selectedItems.length > 0) {
			await onAddItems(selectedItems);
			setSelectedItems([]); // Clear selected items after adding
		} else {
			Alert.alert("No items selected", "Please select items to add.");
		}
	};

	const handleSelectSubCategory = (subCategory) => {
		setSelectedSubCategory(subCategory);
		setCurrentPage(1); // Reset to first page when subcategory changes
	};

	const handleSearch = useCallback(async (query) => {
		if (!query.trim() || query.length < 3) return;
		
		setIsSearchingAmazon(true);
		try {
			const response = await fetch(`${SERVER_URL}/amazon-search`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ 
					query,
					category: selectedCategory || 'All'
				})
			});

			if (response.status === 429) {
				const retryAfter = response.headers.get('retry-after') || 1;
				alert(`Too many requests. Please wait ${retryAfter} second${retryAfter > 1 ? 's' : ''} before trying again.`);
				return;
			}

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.message || 'Search failed');
			}
			
			const data = await response.json();
			if (data.items && Array.isArray(data.items)) {
				setAmazonSearchResults(data.items);
				setCurrentPage(1);
			} else {
				throw new Error('No results found');
			}
		} catch (error) {
			console.error('Amazon search error:', error);
			alert(error.message || 'Failed to search Amazon products. Please try again.');
		} finally {
			setIsSearchingAmazon(false);
		}
	}, [selectedCategory, SERVER_URL]);

	const handleMainSearch = useCallback(async (query) => {
		if (!query.trim() || query.length < 3) return;
		
		setIsSearchingAmazon(true);
		try {
			const response = await fetch(`${SERVER_URL}/amazon-search`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ 
					query,
					category: 'All'
				})
			});

			if (response.status === 429) {
				const retryAfter = response.headers.get('retry-after') || 1;
				alert(`Too many requests. Please wait ${retryAfter} second${retryAfter > 1 ? 's' : ''} before trying again.`);
				return;
			}

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.message || 'Search failed');
			}
			
			const data = await response.json();
			if (data.items && Array.isArray(data.items)) {
				setAmazonSearchResults(data.items);
				setShowSearchResults(true);
				setViewCategories(false);
				setCurrentPage(1);
			} else {
				throw new Error('No results found');
			}
		} catch (error) {
			console.error('Amazon search error:', error);
			alert(error.message || 'Failed to search Amazon products. Please try again.');
		} finally {
			setIsSearchingAmazon(false);
		}
	}, [SERVER_URL]);

	const renderSubCategories = (category) => {
		return (
			<View style={styles.subCategoryListContainer}>
				{subCategories[category].map((subCategory) => (
					<Pressable
						key={subCategory}
						style={({ hovered }) => [
							styles.subCategoryContainer,
							hovered && styles.subCategoryContainerHover,
							selectedSubCategory === subCategory && styles.subCategoryContainerSelected
						]}
						onPress={() => handleSelectSubCategory(subCategory)}
					>
						<Text style={({ hovered }) => [
							styles.subCategoryText,
							hovered && styles.subCategoryTextHover
						]}>
							{subCategory}
						</Text>
					</Pressable>
				))}
			</View>
		);
	};

	const BackButton = ({ onPress, text }) => (
		<Pressable
			style={styles.backButton}
			onPress={onPress}
		>
			<Text style={styles.backButtonText}>{text}</Text>
		</Pressable>
	);

	const renderItem = ({ item }) => (
		<View style={styles.categoryContainer}>
			<Pressable
				onPress={() => handleSelectCategory(item)}
				style={({ hovered }) => [
					styles.categoryButton,
					hovered && styles.categoryContainerHover
				]}
			>
				<Image 
					source={categoryImages[item]} 
					style={styles.categoryImage} 
					resizeMode="cover"
				/>
				<View style={styles.textContainer}>
					<Text style={styles.categoryTitle}>{item}</Text>
				</View>
			</Pressable>
		</View>
	);

	const renderPaginationControls = () => {
		const totalPages = Math.ceil(totalItemsCount / itemsPerPage);
		const maxPagesToShow = 5;
		let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
		let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

		// Adjust start page if we're near the end
		if (endPage - startPage + 1 < maxPagesToShow) {
			startPage = Math.max(1, endPage - maxPagesToShow + 1);
		}

		const pageNumbers = [];
		for (let i = startPage; i <= endPage; i++) {
			pageNumbers.push(i);
		}

		return (
			<View style={styles.paginationContainer}>
				<Pressable 
					onPress={() => setCurrentPage(1)} 
					disabled={currentPage === 1}
					style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
				>
					<Text style={styles.paginationText}>First</Text>
				</Pressable>
				
				<Pressable 
					onPress={handlePreviousPage} 
					disabled={currentPage <= 1}
					style={[styles.paginationButton, currentPage <= 1 && styles.paginationButtonDisabled]}
				>
					<Text style={styles.paginationText}>Previous</Text>
				</Pressable>

				{pageNumbers.map((pageNum) => (
					<Pressable
						key={pageNum}
						onPress={() => setCurrentPage(pageNum)}
						style={[
							styles.paginationButton,
							currentPage === pageNum && styles.paginationButtonActive
						]}
					>
						<Text style={[
							styles.paginationText,
							currentPage === pageNum && styles.paginationTextActive
						]}>
							{pageNum}
						</Text>
					</Pressable>
				))}

				<Pressable 
					onPress={handleNextPage} 
					disabled={currentPage >= totalPages}
					style={[styles.paginationButton, currentPage >= totalPages && styles.paginationButtonDisabled]}
				>
					<Text style={styles.paginationText}>Next</Text>
				</Pressable>
				
				<Pressable 
					onPress={() => setCurrentPage(totalPages)} 
					disabled={currentPage === totalPages}
					style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
				>
					<Text style={styles.paginationText}>Last</Text>
				</Pressable>
			</View>
		);
	};

	return (
		<View style={styles.container}>
			{/* Only show background image when not from registry */}
			{!isFromRegistry && (
				<>
					<Image 
						source={require('../../assets/Landing Background.png')}
						style={styles.backgroundImage}
						resizeMode="cover"
					/>
					<View style={styles.blurOverlay} />
				</>
			)}
			
			{/* Add HeaderBar for standalone version with better visibility */}
			{!isFromRegistry && (
				<View style={styles.headerBarContainer}>
					<HeaderBar />
				</View>
			)}
			
			{isFromLandingPage && (
				<BackButton 
					onPress={handleBackToLanding} 
					text="Back to Landing" 
				/>
			)}
			{isFromHub && (
				<BackButton 
					onPress={handleBackToHub} 
					text="Back to Hub" 
				/>
			)}
			
			{/* Content container with proper constraints */}
			<View style={!isFromRegistry ? styles.standaloneContent : styles.registryContent}>
				{viewCategories ? (
				<>
					<View style={styles.searchContainer}>
					<View style={styles.searchInputContainer}>
						<TextInput
							style={styles.searchInput}
							placeholder="Search Amazon products..."
							value={mainSearchQuery}
							onChangeText={setMainSearchQuery}
						/>
					</View>
					<Pressable 
						style={[
							styles.searchButton,
							(!mainSearchQuery.trim() || mainSearchQuery.length < 3) && styles.searchButtonDisabled
						]}
						onPress={() => handleMainSearch(mainSearchQuery)}
						disabled={!mainSearchQuery.trim() || mainSearchQuery.length < 3 || isSearchingAmazon}
					>
						<Text style={styles.searchButtonText}>
							{isSearchingAmazon ? 'Searching...' : 'Search Amazon'}
						</Text>
					</Pressable>
				</View>

					<FlatList
						data={categories}
						renderItem={renderItem}
						keyExtractor={(item) => item}
						numColumns={isMobile ? 2 : 4}
						columnWrapperStyle={styles.columnWrapper}
						contentContainerStyle={styles.innerContainer}
						showsVerticalScrollIndicator={false}
						initialNumToRender={8}
						maxToRenderPerBatch={8}
						windowSize={5}
					/>
				</>
			) : (
				<View style={styles.itemsContainer}>
					{/* Control Buttons Row */}
					<View style={styles.controlButtonsContainer}>
						<Pressable style={styles.backToCategoriesButtonMobile} onPress={() => {
							setViewCategories(true);
							setShowSearchResults(false);
							setMainSearchQuery("");
							setSelectedCategory(null);
						}}>
							<Text style={styles.backButtonText}>Back to Categories</Text>
						</Pressable>
						
						<Pressable style={styles.addButtonMobile} onPress={handleAddItems}>
							<Text style={styles.addButtonText}>Add Selected Items</Text>
						</Pressable>
					</View>

					{selectedCategory && renderSubCategories(selectedCategory)}

					<View style={styles.paginationContainer}>
						{renderPaginationControls()}
					</View>

					<FlatList
						data={paginatedItems()}
						style={{ 
							width: '100%',
							flex: 1,
							...(isFromRegistry && {
								overflow: 'auto',
								maxHeight: '100vh',
								minHeight: '100vh',
								scrollbarWidth: 'thin',
								scrollbarColor: '#4c956c #e5e7eb'
							})
						}}
						contentContainerStyle={{ 
							paddingBottom: isMobile ? 80 : 40,
							paddingHorizontal: isMobile ? 8 : 12,
							paddingTop: 10,
						}}
						showsVerticalScrollIndicator={isFromRegistry ? true : false}
						showsHorizontalScrollIndicator={false}
						scrollEnabled={true}
						nestedScrollEnabled={false}
						bounces={true}
						removeClippedSubviews={true}
						disableVirtualization={false}
						renderItem={({ item }) => {
							const isSelected = selectedItems.find((selectedItem) => selectedItem.id === item.id);
							return (
								<View
									style={[
										styles.itemCard,
										isSelected ? styles.itemSelected : {},
									]}
								>
									{/* Selection indicator overlay */}
									{isSelected && (
										<View style={styles.selectionOverlay}>
											<View style={styles.checkmarkContainer}>
												<Text style={styles.checkmark}>✓</Text>
											</View>
										</View>
									)}
									<Pressable
										onPress={() => handleSelectItem(item)}
										style={styles.itemCardContent}
									>
									{!visibilitySettings.hideAll && !visibilitySettings.hideImages && item.image && (
										<Image source={{ uri: item.image }} style={styles.itemImage} />
									)}
									{!visibilitySettings.hideAll && (
										<View style={styles.itemTextContainer}>
											<Text style={styles.itemName}>{truncateTitle(item.name)}</Text>
											{!visibilitySettings.hidePrice && (
												<Text style={styles.itemPrice}>{`${item.price}`}</Text>
											)}
											{!isMobile && (
												<Text style={styles.itemDescription}>{truncateDescription(item.description, 15)}</Text>
											)}
											<Text style={styles.itemSubCategory}>{item.subCategory}</Text>
											{item.isAmazonItem && (
												<Text style={styles.amazonLabel}>Amazon Product</Text>
											)}
										</View>
									)}
								</Pressable>
								<Pressable
									style={styles.seeProductButton}
									onPress={() => window.open(item.url, '_blank')}
								>
									<Text style={styles.seeProductButtonText}>View Product</Text>
								</Pressable>
							</View>
						);
						}}
						keyExtractor={(item) => item.id.toString()}
						numColumns={isMobile ? 2 : 3}
					/>

					<View style={styles.paginationContainer}>
						{renderPaginationControls()}
					</View>

				</View>
			)}
			<View style={styles.disclaimerContainer}>
				<Text style={styles.disclaimer}>Disclaimer: As an Amazon Associate MyTassel earns commissions from qualifying purchases on our site.</Text>
			</View>
			</View> {/* Close content container */}
		</View>
	);
};

const fontFamilies = {
	Bodoni72Smallcaps: "Bodoni72Smallcaps",
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		width: "100%",
		height: "100%",
		top: 0,
		position: 'relative',
		backgroundColor: 'transparent',
		overflow: 'visible',
	},
	standaloneContent: {
		width: isMobile ? "98%" : isTablet ? "96%" : "95%",
		alignSelf: "center",
		backgroundColor: "#fefde2",
		borderRadius: isMobile ? 15 : isTablet ? 18 : 20,
		marginTop: isMobile ? 5 : isTablet ? 8 : 10,
		marginBottom: isMobile ? 5 : isTablet ? 8 : 10,
		paddingHorizontal: isMobile ? 10 : isTablet ? 12 : 15,
		paddingVertical: isMobile ? 8 : isTablet ? 9 : 10,
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.25,
		shadowRadius: 3.84,
		elevation: 5,
	},
	registryContent: {
		flex: 1,
		width: '100%',
	},
	headerBarContainer: {
		backgroundColor: 'rgba(76, 149, 108, 0.9)', // Dark green semi-transparent background
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.3,
		shadowRadius: 4,
		elevation: 8,
		zIndex: 1000,
		borderBottomWidth: 2,
		borderBottomColor: 'rgba(76, 149, 108, 1)',
	},
	backgroundImage: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		width: '100%',
		height: '100%',
	},
	blurOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(255, 255, 255, 0.8)',
		backdropFilter: 'blur(10px)',
	},
	innerContainer: {
		flexDirection: "row",
		flexWrap: "wrap",
		justifyContent: "center",
		alignItems: "center",
		paddingHorizontal: isMobile ? 10 : 40,
		paddingVertical: isMobile ? 10 : 20,
		width: "100%",
		gap: isMobile ? 10 : 20,
	},
	categoryContainer: {
		backgroundColor: "#ffffff",
		borderRadius: 15,
		elevation: 3,
		shadowColor: "#000",
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.2,
		shadowRadius: 4,
		width: isMobile ? "45%" : "22%",
		aspectRatio: 4/3, // More rectangular aspect ratio
		overflow: "hidden",
		position: "relative",
		margin: 0, // Remove margin since we're using gap in container
		minWidth: isMobile ? 120 : isSmallDesktop ? 280 : 350, // Ensure minimum width
		maxWidth: isMobile ? 140 : isSmallDesktop ? 250 : 350, // Ensure maximum width
		height: isMobile ? 200 : isSmallDesktop ? 400 : 500,
	},
	categoryButton: {
		width: '100%',
		height: '100%',
		alignItems: 'center',
		justifyContent: 'flex-end',
		position: 'relative',
	},
	categoryImage: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		width: '100%',
		height: '100%',
	},
	textContainer: {
		backgroundColor: "rgba(255, 255, 255, 0.95)",
		width: "100%",
		paddingVertical: isMobile ? 12 : 15,
		alignItems: "center",
		borderTopWidth: 1,
		borderTopColor: "rgba(0,0,0,0.1)",
	},
	categoryTitle: {
		fontSize: isMobile ? 16 : 22,
		fontWeight: "600",
		color: "#333",
		textAlign: "center",
		fontFamily: fontFamilies.Bodoni72Smallcaps,
		letterSpacing: 0.5,
	},
	itemsContainer: {
		marginTop: isMobile ? 5 : 10,
		flexDirection: "column",
		justifyContent: "flex-start",
		alignItems: "center",
		paddingHorizontal: 0,
		paddingBottom: 0,
		width: "100%",
	},
	itemCard: {
		backgroundColor: "#ffffff",
		borderRadius: 16,
		padding: isMobile ? 12 : 20,
		marginVertical: isMobile ? 8 : 12,
		marginHorizontal: isMobile ? 6 : 12,
		shadowColor: "#000000",
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.1,
		shadowRadius: 8,
		elevation: 3,
		alignItems: "center",
		justifyContent: 'space-between',
		maxWidth: isMobile ? "45%" : "30%",
		minWidth: isMobile ? "45%" : "30%",
		minHeight: isMobile ? 280 : 420,
		transition: 'all 0.2s ease',
		overflow: 'hidden',
		border: '1px solid #f1f5f9',
	},
	itemCardContent: {
		flex: 1,
		alignItems: "center",
		justifyContent: "flex-start",
		width: "100%",
	},
	itemTextContainer: {
		alignItems: "center",
		justifyContent: "center",
		flex: 1,
		width: "100%",
	},
	itemSelected: {
		borderColor: "#4c956c",
		borderWidth: 3,
		backgroundColor: "#f0f9ff",
		transform: [{ scale: 1.02 }],
		shadowColor: "#4c956c",
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.3,
		shadowRadius: 8,
		elevation: 8,
	},
	itemImage: {
		width: "100%",
		height: isMobile ? 140 : 200,
		resizeMode: "cover",
		borderRadius: 12,
		marginBottom: isMobile ? 8 : 12,
		backgroundColor: '#f8fafc',
	},
	itemName: {
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontSize: isMobile ? 14 : 16,
		fontWeight: "600",
		color: "#1e293b",
		textAlign: "center",
		marginBottom: isMobile ? 4 : 8,
		lineHeight: isMobile ? 20 : 22,
	},

	itemDescription: {
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontSize: isMobile ? 12 : 14,
		color: "#64748b",
		textAlign: "center",
		marginBottom: isMobile ? 6 : 10,
		lineHeight: isMobile ? 18 : 20,
	},

	itemPrice: {
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontSize: isMobile ? 14 : 16,
		fontWeight: "700",
		color: "#059669",
		textAlign: "center",
		marginBottom: isMobile ? 4 : 8,
	},
	backButton: {
		position: 'absolute',
		top: isMobile ? 5 : isSmallDesktop ? 10 : 20,
		left: isMobile ? 5 : isSmallDesktop ? 10 : 20,
		backgroundColor: '#4c956c',
		padding: 10,
		borderRadius: 5,
		zIndex: 1000,
	},
	controlButtonsContainer: {
		flexDirection: 'row',
		justifyContent: isMobile ? 'space-between' : 'center',
		alignItems: 'center',
		paddingHorizontal: 20,
		paddingVertical: 10,
		marginBottom: 10,
		width: isMobile ? '100%' : '60%',
		alignSelf: 'center',
		gap: isMobile ? 0 : 20,
	},
	backToCategoriesButtonMobile: {
		backgroundColor: "#6b7280",
		paddingVertical: isMobile ? 10 : 12,
		paddingHorizontal: isMobile ? 16 : 24,
		borderRadius: 10,
		flex: isMobile ? 1 : 0,
		marginRight: isMobile ? 8 : 0,
		alignItems: 'center',
		minWidth: isMobile ? 'auto' : 150,
		border: 'none',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
	},
	addButtonMobile: {
		backgroundColor: "#10b981",
		paddingVertical: isMobile ? 10 : 12,
		paddingHorizontal: isMobile ? 16 : 24,
		borderRadius: 10,
		flex: isMobile ? 1 : 0,
		marginLeft: isMobile ? 8 : 0,
		alignItems: 'center',
		minWidth: isMobile ? 'auto' : 150,
		border: 'none',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
	},
	backToCategoriesButton: {
		position: "absolute",
		top: isMobile ? 50 : 20,
		left: 20,
		backgroundColor: "#4c956c",
		paddingVertical: 10,
		paddingHorizontal: 20,
		borderRadius: 20,
		zIndex: 10,
	},
	backButtonText: {
		color: '#ffffff',
		fontSize: isMobile ? 13 : 15,
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontWeight: '600',
	},
	addButton: {
		position: "absolute",
		top: isMobile ? 0 : 20, 
		right: 20,
		backgroundColor: "#4c956c",
		paddingVertical: 10,
		paddingHorizontal: 20,
		borderRadius: 20,
		zIndex: 10,
	},
	addButtonText: {
		fontSize: isMobile ? 13 : 15,
		color: "#ffffff",
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontWeight: '600',
	},
	searchContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		width: isMobile ? "95%" : "80%",
		marginVertical: isMobile ? 16 : 24,
		alignSelf: 'center',
		gap: 12,
	},
	searchInputContainer: {
		flex: 1,
		flexDirection: 'row',
		marginRight: 10,
	},
	searchInput: {
		flex: 1,
		fontSize: isMobile ? 14 : 16,
		padding: isMobile ? 12 : 16,
		borderWidth: 1,
		borderColor: "#e2e8f0",
		borderRadius: 12,
		backgroundColor: "#ffffff",
		fontFamily: "system-ui, -apple-system, sans-serif",
		color: "#1e293b",
		shadowColor: "#000000",
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.05,
		shadowRadius: 3,
		elevation: 1,
	},
	searchButton: {
		backgroundColor: "#4c956c",
		paddingVertical: isMobile ? 12 : 16,
		paddingHorizontal: isMobile ? 16 : 24,
		borderRadius: 12,
		justifyContent: 'center',
		alignItems: 'center',
		minWidth: isMobile ? 80 : 100,
		transition: 'all 0.2s ease',
		border: 'none',
		cursor: 'pointer',
	},
	searchButtonDisabled: {
		backgroundColor: "#94a3b8",
		opacity: 0.6,
	},
	searchButtonText: {
		color: "#ffffff",
		fontSize: isMobile ? 12 : 14,
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontWeight: '600',
	},
	paginationContainer: {
		flexDirection: 'row',
		justifyContent: 'center',
		alignItems: 'center',
		paddingVertical: isMobile ? 5 : 8,
		gap: isMobile ? 5 : 10,
		width: '100%',
		marginHorizontal: isMobile ? 10 : 0,
		flexWrap: 'wrap', // Allow wrapping on mobile
	},
	paginationButton: {
		paddingHorizontal: isMobile ? 10 : 16,
		paddingVertical: isMobile ? 8 : 10,
		backgroundColor: '#4c956c',
		borderRadius: 8,
		minWidth: isMobile ? 36 : 44,
		alignItems: 'center',
		marginHorizontal: isMobile ? 3 : 4,
		border: 'none',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
	},
	paginationButtonDisabled: {
		backgroundColor: '#94a3b8',
		opacity: 0.6,
		cursor: 'not-allowed',
	},
	paginationButtonActive: {
		backgroundColor: '#2c6e3e',
		transform: 'scale(1.05)',
	},
	paginationText: {
		color: '#ffffff',
		fontSize: isMobile ? 12 : 16,
	},
	paginationTextActive: {
		fontWeight: 'bold',
	},
	seeProductButton: {
		marginTop: isMobile ? 8 : 12,
		paddingVertical: isMobile ? 8 : 12,
		paddingHorizontal: isMobile ? 12 : 20,
		backgroundColor: '#4c956c',
		borderRadius: isMobile ? 8 : 10,
		alignSelf: 'center',
		minWidth: isMobile ? 100 : 120,
		maxWidth: '90%',
		transition: 'all 0.2s ease',
		border: 'none',
		cursor: 'pointer',
	},
	seeProductButtonText: {
		color: '#ffffff',
		fontSize: isMobile ? 12 : 14,
		textAlign: 'center',
		fontWeight: '600',
		fontFamily: "system-ui, -apple-system, sans-serif",
	},
	disclaimer: {
		flex: 1,
		fontFamily: "Orpheus Pro",
		fontSize: 18,
		textAlign: "center",
		justifyContent: "center",
		color: "#4C956C", // Or any other color that fits your theme
		marginBottom: 15,
		fontStyle: "italic",
		top: 25,
	},
	disclaimerContainer: {
		justifyContent: "center",
		alignItems: "center",
		marginTop: 20,
		marginBottom: 20,
	},
	itemHover: {
		transform: [{ scale: 1.025 }],
	},
	subCategoryContainer: {
		backgroundColor: "#f1f5f9",
		borderRadius: isMobile ? 8 : 12,
		margin: isMobile ? 4 : 6,
		overflow: "hidden",
		border: '1px solid #e2e8f0',
		width: "auto",
		height: isMobile ? 36 : 44,
		paddingHorizontal: isMobile ? 12 : 16,
		paddingVertical: isMobile ? 8 : 10,
		alignItems: "center",
		justifyContent: "center",
		transition: 'all 0.2s ease',
		cursor: 'pointer',
	},
	subCategoryContainerHover: {
		backgroundColor: "#e2e8f0",
		borderColor: "#cbd5e1",
	},
	subCategoryContainerSelected: {
		backgroundColor: "#4c956c",
		borderColor: "#2c6e3e",
	},
	subCategoryText: {
		fontSize: isMobile ? 12 : 14,
		fontWeight: "500",
		textAlign: "center",
		fontFamily: "system-ui, -apple-system, sans-serif",
		color: "#475569",
		whiteSpace: 'nowrap',
	},
	subCategoryTextHover: {
		fontSize: isMobile ? 12 : 14,
		fontWeight: "600",
		textAlign: "center",
		fontFamily: "system-ui, -apple-system, sans-serif",
		color: "#334155",
		whiteSpace: 'nowrap',
	},
	subCategoryListContainer: {
		flexDirection: "row",
		justifyContent: "center",
		alignItems: "center",
		paddingVertical: isMobile ? 12 : 16,
		flexWrap: "wrap",
		gap: isMobile ? 8 : 12,
		width: "100%",
		paddingHorizontal: isMobile ? 16 : "10%",
		marginBottom: isMobile ? 16 : 20,
		marginHorizontal: isMobile ? 8 : 16,
	},
	amazonLabel: {
		fontSize: isMobile ? 12 : 14,
		color: '#4c956c',
		fontWeight: 'bold',
		marginTop: 5,
		fontFamily: "Orpheus Pro",
	},
	categoryPicker: {
		width: 150,
		marginLeft: 10,
	},
	columnWrapper: {
		justifyContent: 'center',
		gap: isMobile ? 10 : 20,
	},
	selectionOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(76, 149, 108, 0.1)',
		borderRadius: 16,
		zIndex: 10,
		pointerEvents: 'none',
	},
	checkmarkContainer: {
		position: 'absolute',
		top: 8,
		right: 8,
		width: isMobile ? 24 : 30,
		height: isMobile ? 24 : 30,
		backgroundColor: '#4c956c',
		borderRadius: isMobile ? 12 : 15,
		justifyContent: 'center',
		alignItems: 'center',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.25,
		shadowRadius: 3,
		elevation: 5,
	},
	checkmark: {
		color: 'white',
		fontSize: isMobile ? 16 : 18,
		fontWeight: 'bold',
		textAlign: 'center',
	},
});

export default Catalogue;